import { CalendarDays, ChevronDown, Send } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { MESSAGE_LOG_FILTERS, MESSAGE_LOG_FILTER_LABELS, type MessageLogCounts, type MessageLogFilter } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { DatePicker } from '../../../../components/DatePicker';
import { SearchInput } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { Select } from '../../../../components/Select';
import { errorMessage } from '../../../../lib/api-client';
import { addDhakaDays, dhakaToday } from '../../../../lib/dhaka-time';
import { EMPTY, formatNumber } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useSendTestNotification } from '../../../notifications/api/notifications-api';
import { FILTER_TONES, type EmailDeliverySearch } from './email-delivery-address';
import styles from './EmailToolbar.module.css';

/** 06-qol §6: type-ahead waits this long after the last key. */
const SEARCH_DEBOUNCE_MS = 250;
const ANY = '';

/** The status pills with their counts (one GROUP BY on the server), then the search, Sent to and When. */
export function EmailToolbar({
  search,
  counts,
  onChange,
}: {
  readonly search: EmailDeliverySearch;
  readonly counts: MessageLogCounts | undefined;
  readonly onChange: (next: Partial<EmailDeliverySearch>) => void;
}): ReactNode {
  const audienceLabel = useId();
  const [typed, setTyped] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const onType = (value: string): void => {
    setTyped(value);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onChange({ q: value.trim() === '' ? undefined : value.trim() }), SEARCH_DEBOUNCE_MS);
  };

  const pill = (filter: MessageLogFilter | undefined, label: string): ReactNode => {
    const count = filter ? counts?.[filter] : counts?.all;
    const isOn = search.state === filter;
    return (
      <button
        key={filter ?? 'all'}
        type="button"
        className={styles.pill}
        aria-pressed={isOn}
        data-tone={filter ? FILTER_TONES[filter] : undefined}
        data-alarm={filter === 'attention' && (count ?? 0) > 0 ? true : undefined}
        onClick={() => onChange({ state: filter })}
      >
        {label}
        <span className={styles.count}>{count === undefined ? EMPTY : formatNumber(count)}</span>
      </button>
    );
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.pills} role="group" aria-label="Show emails that are">
        {MESSAGE_LOG_FILTERS.map((filter) => pill(filter, MESSAGE_LOG_FILTER_LABELS[filter]))}
        {pill(undefined, 'Every email')}
      </div>
      <div className={styles.filters}>
        <span className={styles.search}>
          <SearchInput
            aria-label="Search emails"
            placeholder="Search subject, name or address"
            value={typed ?? search.q ?? ''}
            onChange={(event) => onType(event.target.value)}
            onClear={() => onType('')}
            data-primary-search
          />
        </span>
        <span className={styles.filter}>
          <span id={audienceLabel} className="sr-only">
            Sent to
          </span>
          <Select
            describedBy={audienceLabel}
            label="Sent to"
            value={search.audience ?? ANY}
            options={[
              { value: ANY, label: 'Sent to anyone' },
              { value: 'candidates', label: 'Sent to candidates' },
              { value: 'staff', label: 'Sent to staff' },
            ]}
            onChange={(value) => onChange({ audience: value === 'candidates' || value === 'staff' ? value : undefined })}
          />
        </span>
        <WhenFilter search={search} onChange={onChange} />
      </div>
    </div>
  );
}

function whenLabel(search: EmailDeliverySearch): string {
  if (search.from === undefined && search.to === undefined) return 'Any day';
  const today = dhakaToday();
  if (search.from === today && (search.to === undefined || search.to === today)) return 'Today';
  if (search.to === undefined || search.to === today) {
    if (search.from === addDhakaDays(today, -6)) return 'Last 7 days';
    if (search.from === addDhakaDays(today, -29)) return 'Last 30 days';
  }
  return `${search.from ?? '…'} – ${search.to ?? '…'}`;
}

/** "When": today, the last 7 or 30 days, or two dates (Asia/Dhaka days). Opens beside its button. */
function WhenFilter({ search, onChange }: { readonly search: EmailDeliverySearch; readonly onChange: (next: Partial<EmailDeliverySearch>) => void }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const today = dhakaToday();
  const [from, setFrom] = useState(search.from ?? '');
  const [to, setTo] = useState(search.to ?? '');
  const isBackwards = from !== '' && to !== '' && to < from;
  const pick = (next: { from?: string; to?: string }): void => {
    close();
    onChange({ from: next.from, to: next.to });
  };
  return (
    <>
      <Button ref={triggerRef} size="sm" icon={CalendarDays} aria-expanded={isOpen} onClick={() => {
        setFrom(search.from ?? '');
        setTo(search.to ?? '');
        toggle();
      }}>
        {whenLabel(search)}
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={300} label="When">
        <div className={styles.when}>
          <div className={styles.quick}>
            <Button variant="ghost" size="sm" onClick={() => pick({})}>Any day</Button>
            <Button variant="ghost" size="sm" onClick={() => pick({ from: today, to: today })}>Today</Button>
            <Button variant="ghost" size="sm" onClick={() => pick({ from: addDhakaDays(today, -6) })}>Last 7 days</Button>
            <Button variant="ghost" size="sm" onClick={() => pick({ from: addDhakaDays(today, -29) })}>Last 30 days</Button>
          </div>
          <DatePicker label="From" value={from} max={today} onChange={setFrom} />
          <DatePicker label="Until" value={to} max={today} onChange={setTo} isInvalid={isBackwards} />
          {isBackwards && <span className={styles.error}>Choose an Until date on or after the From date.</span>}
          <Button variant="solid" size="sm" disabled={isBackwards || (from === '' && to === '')} onClick={() => pick({ from: from === '' ? undefined : from, to: to === '' ? undefined : to })}>
            Show these dates
          </Button>
        </div>
      </Popover>
    </>
  );
}

/** Long enough to see the delay work, short enough that QA does not go for lunch. */
const TEST_ESCALATION_SECONDS = 60;

/**
 * "Send myself a test" (QA tests 1 and 5): a real notification through the real engine, the escalation chain too if
 * asked. Opens its small panel right under the button.
 */
export function TestSendButton({ email }: { readonly email: string | null }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [escalate, setEscalate] = useState(false);
  const sendTest = useSendTestNotification();
  const showToast = useToasts((state) => state.show);
  const run = (): void => {
    close();
    sendTest.mutate(escalate ? { escalateAfterSeconds: TEST_ESCALATION_SECONDS } : {}, {
      onSuccess: (result) =>
        showToast({
          tone: 'success',
          message: `Test sent by ${result.channels.join(' and ')}`,
          ...(escalate ? { detail: `The escalation fires in ${TEST_ESCALATION_SECONDS} seconds and copies you, because an escalation always tells the person it is about.` } : {}),
        }),
      onError: (error) => showToast({ tone: 'danger', message: 'The test was not sent', detail: errorMessage(error) }),
    });
  };
  return (
    <>
      <Button ref={triggerRef} variant="solid" icon={Send} isPending={sendTest.isPending} aria-expanded={isOpen} onClick={toggle}>
        Send myself a test <ChevronDown size={14} aria-hidden />
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={340} label="Send yourself a test">
        <div className={styles.when}>
          <h3 className={styles.popTitle}>Send yourself a test</h3>
          <p className={styles.popBody}>
            Goes through the real engine — your preferences, the real template, the real queue — to {email ?? 'your address'} and your bell.
          </p>
          <Checkbox checked={escalate} onChange={setEscalate} label="Also test the escalation chain" />
          <p className={styles.popBody}>Fires a test escalation {TEST_ESCALATION_SECONDS} seconds later, to prove the delay works.</p>
          <Button variant="solid" size="sm" icon={Send} onClick={run}>
            Send test
          </Button>
        </div>
      </Popover>
    </>
  );
}
